<script lang="ts">
  import { onMount } from 'svelte';
  import type { Flashcard } from '../types/flashcard';
  import { Volume2, CheckCircle2, XCircle, AlertCircle, Sparkles } from 'lucide-svelte';
  import { _ } from 'svelte-i18n';
  import Button from './ui/button.svelte';
  import Card from './ui/card.svelte';
  import { speakFlashcard, ttsService } from '../lib/tts';
  import {
    saveReview,
    getSchedulingOptions,
    RATING_LABELS,
    formatInterval,
  } from '../lib/fsrs-scheduler';
  import { Rating } from 'ts-fsrs';
  import { userSettings } from '../stores/settings';

  let { flashcardList, onBack, onComplete }: {
    flashcardList: Flashcard[],
    onBack: () => void,
    onComplete?: () => void
  } = $props();

  let isFlipping = $state(false);
  let isSpeaking = $state(false);
  let isSubmitting = $state(false);
  let showFront = $state(true);
  let currentIndex = $state(0);
  let reviewedCount = $state(0);
  let sessionStats = $state({
    again: 0,
    hard: 0,
    good: 0,
    easy: 0,
  });

  let currentCard = $derived(flashcardList[currentIndex]);
  let totalCards = $derived(flashcardList.length);
  let isComplete = $derived(currentIndex >= totalCards);

  // Get scheduling options for current card
  let schedulingOptions = $state<ReturnType<typeof getSchedulingOptions> | null>(null);

  onMount(() => {
    loadSchedulingOptions();
  });

  function loadSchedulingOptions() {
    if (currentCard) {
      schedulingOptions = getSchedulingOptions(currentCard);
    }
  }

  function handleCardFlip() {
    isFlipping = true;
    setTimeout(() => {
      showFront = !showFront;
      isFlipping = false;
    }, 300);
  }

  async function handleRating(rating: Rating) {
    if (!currentCard || isSubmitting) return;

    isSubmitting = true;

    try {
      // Save the review to database
      const result = await saveReview(currentCard.id, rating);

      if (result.success) {
        // Update session stats
        if (rating === Rating.Again) sessionStats.again++;
        else if (rating === Rating.Hard) sessionStats.hard++;
        else if (rating === Rating.Good) sessionStats.good++;
        else if (rating === Rating.Easy) sessionStats.easy++;

        reviewedCount++;

        // Move to next card
        currentIndex++;
        showFront = true;

        // Load scheduling options for next card
        if (currentIndex < totalCards) {
          loadSchedulingOptions();
        } else {
          // Session complete
          if (onComplete) {
            onComplete();
          }
        }
      } else {
        console.error('Failed to save review:', result.error);
        alert('Chyba při ukládání. Zkuste to prosím znovu.');
      }
    } catch (error) {
      console.error('Error in handleRating:', error);
      alert('Chyba při ukládání. Zkuste to prosím znovu.');
    } finally {
      isSubmitting = false;
    }
  }

  async function handlePlayAudio() {
    if (!currentCard) return;

    // Cancel any ongoing speech
    if (isSpeaking) {
      ttsService.cancel();
      isSpeaking = false;
      return;
    }

    try {
      isSpeaking = true;
      // Get primary language from user settings (default to 'cs')
      const primaryLanguage = $userSettings?.preferred_language || 'cs';

      const textToSpeak = showFront ? currentCard.primary : currentCard.target;
      const languageCode = showFront ? primaryLanguage : currentCard.targetLanguage.code;
      await speakFlashcard(textToSpeak, languageCode);
    } catch (error) {
      console.error('TTS error:', error);
    } finally {
      isSpeaking = false;
    }
  }

  function getRatingButtonClass(rating: Rating): string {
    const config = RATING_LABELS[rating as 1 | 2 | 3 | 4];
    const baseClass = 'flex-1 flex flex-col items-center gap-2 py-4 px-3 rounded-lg transition-all';

    if (config.color === 'destructive') {
      return `${baseClass} bg-destructive/10 hover:bg-destructive/20 border-2 border-destructive/30 text-destructive`;
    } else if (config.color === 'warning') {
      return `${baseClass} bg-warning/10 hover:bg-warning/20 border-2 border-warning/30 text-warning`;
    } else if (config.color === 'primary') {
      return `${baseClass} bg-primary/10 hover:bg-primary/20 border-2 border-primary/30 text-primary`;
    } else {
      return `${baseClass} bg-secondary/10 hover:bg-secondary/20 border-2 border-secondary/30 text-secondary`;
    }
  }

  function getRatingIcon(rating: Rating) {
    if (rating === Rating.Again) return XCircle;
    if (rating === Rating.Hard) return AlertCircle;
    if (rating === Rating.Good) return CheckCircle2;
    return Sparkles;
  }
</script>

<div class="h-full bg-gradient-primary safe-top safe-bottom pb-28 overflow-y-auto">
  <div class="container mx-auto px-4 py-8 max-w-md">
    <!-- Header -->
    <div class="mb-8">
      <h1 class="text-2xl font-bold text-center mb-4">
        FSRS Opakování
      </h1>

      <div class="flex items-center justify-center gap-6 text-sm">
        <div class="bg-white/20 backdrop-blur rounded-full px-4 py-2">
          <span class="font-medium">
            {currentIndex} / {totalCards}
          </span>
        </div>
        <div class="flex gap-3">
          <span class="text-destructive">✗ {sessionStats.again}</span>
          <span class="text-warning">△ {sessionStats.hard}</span>
          <span class="text-primary">✓ {sessionStats.good}</span>
          <span class="text-secondary">★ {sessionStats.easy}</span>
        </div>
      </div>
    </div>

    {#if isComplete}
      <!-- Completion screen -->
      <Card class="p-8 text-center bg-white/10 backdrop-blur border-white/20">
        <div class="text-6xl mb-4">🎉</div>
        <h2 class="section-title">Skvělá práce!</h2>
        <p class="text-white/80 mb-6">
          Zopakovali jste {reviewedCount} {reviewedCount === 1 ? 'kartu' : reviewedCount < 5 ? 'karty' : 'karet'}.
        </p>

        <div class="grid grid-cols-2 gap-4 mb-6">
          <div class="bg-destructive/20 rounded-lg p-4">
            <div class="text-2xl font-bold text-destructive">{sessionStats.again}</div>
            <div class="text-sm">Znovu</div>
          </div>
          <div class="bg-warning/20 rounded-lg p-4">
            <div class="text-2xl font-bold text-warning">{sessionStats.hard}</div>
            <div class="text-sm">Těžké</div>
          </div>
          <div class="bg-primary/20 rounded-lg p-4">
            <div class="text-2xl font-bold text-primary">{sessionStats.good}</div>
            <div class="text-sm">Dobré</div>
          </div>
          <div class="bg-secondary/20 rounded-lg p-4">
            <div class="text-2xl font-bold text-secondary">{sessionStats.easy}</div>
            <div class="text-sm">Snadné</div>
          </div>
        </div>

        <Button onclick={() => onBack()} class="w-full">
          Hotovo
        </Button>
      </Card>
    {:else if !currentCard}
      <!-- No cards -->
      <Card class="p-8 text-center bg-white/10 backdrop-blur border-white/20">
        <p class="text-white">Žádné karty k opakování</p>
      </Card>
    {:else}
      <!-- Flashcard -->
      <div
        class="relative h-80 cursor-pointer mb-6 {isFlipping ? 'animate-flip' : ''}"
        onclick={handleCardFlip}
        onkeydown={(e: KeyboardEvent) => e.key === 'Enter' || e.key === ' ' ? handleCardFlip() : null}
        role="button"
        tabindex="0"
      >
        <Card class="h-full p-8 bg-gradient-card backdrop-blur border-white/20 flex items-center justify-center">
          <!-- Audio button -->
          <Button
            onclick={(e) => { e.stopPropagation(); handlePlayAudio(); }}
            variant="ghost"
            size="icon"
            class="absolute top-4 right-4 {isSpeaking ? 'bg-white/20 animate-pulse' : ''}"
            aria-label={isSpeaking ? $_('aria.stop_audio') : $_('aria.read_aloud')}
          >
            <Volume2 class="w-5 h-5 {isSpeaking ? 'text-primary' : ''}" />
          </Button>

          <div class="text-center">
            {#if showFront}
              <p class="text-3xl font-bold mb-4">
                {currentCard.primary}
              </p>
              <p class="text-sm">
                Klepněte pro zobrazení odpovědi
              </p>
            {:else}
              <p class="text-3xl font-bold mb-2">
                {currentCard.target}
              </p>
              {#if currentCard.phonetic}
                <p class="text-lg italic mb-4">
                  {currentCard.phonetic}
                </p>
              {/if}
              <p class="text-sm mt-4">
                Jak dobře jste si vzpomněli?
              </p>
            {/if}
          </div>
        </Card>
      </div>

      <!-- Rating buttons (only shown after flip) -->
      {#if !showFront && schedulingOptions}
        <div class="space-y-3">
          <div class="grid grid-cols-2 gap-3">
            <!-- Again -->
            {#each [Rating.Again, Rating.Hard, Rating.Good, Rating.Easy] as rating}
              {@const Icon = getRatingIcon(rating)}
              <button
                onclick={() => handleRating(rating)}
                disabled={isSubmitting}
                class={getRatingButtonClass(rating)}
              >
                <Icon class="w-6 h-6" />
                <span class="font-semibold text-sm">{RATING_LABELS[rating as 1 | 2 | 3 | 4].label}</span>
                <span class="text-xs opacity-80">
                  {formatInterval(schedulingOptions[rating as 1 | 2 | 3 | 4].card.scheduled_days)}
                </span>
              </button>
            {/each}
          </div>

          <div class="text-center text-xs">
            Intervaly: kdy uvidíte kartu příště
          </div>
        </div>
      {/if}
    {/if}
  </div>
</div>
